// Shelf Service - API interactions for shelf management

import {
  Shelf,
  PageShelfRequest,
} from '../types/shelf';
import { ApiResponse, PaginatedResponse } from '../types/index';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import {API_ENDPOINTS} from "../utils/apiEndpoints";
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

/**
 * Service class for Shelf operations
 * Supports both Mock data and real API calls
 */
export class ShelfService {
  private static instance: ShelfService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;
  
  /**
   * Get singleton instance
   */
  public static getInstance(): ShelfService {
    if (!ShelfService.instance) {
      ShelfService.instance = new ShelfService();
    }
    return ShelfService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    ShelfService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return ShelfService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 分页查询货架
   */
  async pageQueryShelves(query: PageShelfRequest): Promise<ApiResponse<PaginatedResponse<Shelf>>> {
    try {
      if (ShelfService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(800);
        
        // Import mock data dynamically
        const { getMockShelfListResponse } = await import('../mock/shelfData');
        return getMockShelfListResponse(query);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.SHELF.PAGE, query);
        return response.data;
      }
    } catch (error) {
      console.error('分页查询货架失败:', error);
      return createErrorResponse(error.message, 'SERVER_ERROR');
    }
  }

  /**
   * 根据ID查询货架详情
   */
  async getShelfById(id: number): Promise<ApiResponse<Shelf>> {
    try {
      if (ShelfService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(600);
        
        // Import mock data dynamically
        const { getMockShelfDetailResponse } = await import('../mock/shelfData');
        return getMockShelfDetailResponse(id);
      } else {
        // Call real API
        const response = await httpClient.get(`/wms/api/shelf/${id}`);
        return response.data;
      }
    } catch (error) {
      console.error('根据ID查询货架失败:', error);
      return createErrorResponse(error.message, 'SERVER_ERROR');
    }
  }

  /**
   * 导出货架使用统计
   */
  async exportStatistics(): Promise<ApiResponse<{ fileName: string, downloadUrl: string }>> {
    try {
      if (ShelfService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);
        
        // Generate file name
        const nowString = new Date().toISOString().replace('T', ' ').substring(0, 19);
        const fileName = `Shelf_Statistics_${nowString.replace(/[\s:-]/g, '')}.xlsx`;

        return createSuccessResponse({
          fileName,
          downloadUrl: `/api/v1/downloads/${fileName}`,
        });
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.SHELF.DOWNLOAD_STATISTICS);
        return response.data;
      }
    } catch (error) {
      console.error('导出货架使用统计失败:', error);
      return createErrorResponse('导出数据失败');
    }
  }
}

// Export singleton instance
export default ShelfService.getInstance();